import React, { useMemo } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import useVehicleSaleReport from './hooks/useVehicleSaleReport';
import VehicleSaleReportTable from './components/VehicleSaleReportTable';
import { getColumns } from './helpers/getColumns';
import { useLanguage } from '../../../contexts/LanguageContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { Roles } from '../../../utils/roleEnums';
import { useGetDealerBySapDropdownListQuery } from '../../../api/endpoints/dealer';
import { useLazyExportVehicleSaleReportToExcelQuery } from '../../../api/endpoints/vehicleSaleReport';
import GenericFileExportButton from '../../../components/common/GenericFileExportButton';

export default function VehicleSaleReportPage() {
    const { th } = useLanguage();
    const { hasAdminRole, hasVehicleSalesManagerRole, hasRole } = usePermissions();

    // Satis ekibi: Admin / RegionalManager / VehicleSalesManager.
    // Bayi rollerine bayi filtresi gosterilmez (backend zaten kendi kayitlarina kisitliyor).
    const isSalesTeam = hasAdminRole() || hasVehicleSalesManagerRole() || hasRole(Roles.REGIONAL_MANAGER);

    const {
        items,
        totalCount,
        isFetching,
        lazyParams,
        onPage,
        activeFilters,
        filterOptions,
        locale,
        handleFilterChange,
        resetFilters,
        sortField,
        sortOrder,
        onSort,
        exportParams,
        dealerBranchCode,
        onDealerBranchCodeChange
    } = useVehicleSaleReport();

    const columns = useMemo(() => getColumns(th), [th]);

    // Bayi filtresi: "BosBranchCode - Bayi Adi" gosterilir.
    // dealerBranchCode (DealerBranchCode) olarak bayinin sube/BOS kodu (sapDealerId) gonderilir.
    const { data: dealerList = [] } = useGetDealerBySapDropdownListQuery(
        { RowCount: 9999, SearchText: '' },
        { skip: !isSalesTeam }
    );

    const dealerDropdownOptions = useMemo(() => {
        return (Array.isArray(dealerList) ? dealerList : [])
            .filter((d) => d?.sapDealerId != null)
            .map((d) => {
                const code = String(d.sapDealerId);
                const name = d.name ?? d.translatedText ?? '';
                return {
                    label: name ? `${code} - ${name}` : code,
                    value: code
                };
            });
    }, [dealerList]);

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('vehicleSaleReport.title')}</h1>
                <div className="flex gap-2 align-items-center">
                    {isSalesTeam && (
                        <Dropdown
                            value={dealerBranchCode}
                            options={dealerDropdownOptions}
                            onChange={(e) => onDealerBranchCodeChange(e.value || null)}
                            placeholder={th('vehicleSaleReport.dealerFilter')}
                            filter
                            showClear
                            virtualScrollerOptions={{ itemSize: 38 }}
                            style={{ minWidth: '18rem' }}
                        />
                    )}
                    <Button
                        icon="pi pi-filter-slash"
                        className="p-button-outlined p-button-secondary"
                        onClick={resetFilters}
                        tooltip={th('filter.reset')}
                        tooltipOptions={{ position: 'left' }}
                    />
                    <GenericFileExportButton
                        useExportQuery={useLazyExportVehicleSaleReportToExcelQuery}
                        exportParams={exportParams}
                        buttonLabel={th('common.export')}
                        buttonClassName="p-button-success"
                        icon="pi pi-file-excel"
                        defaultFileName="AracSatisRaporu"
                    />
                </div>
            </div>

            <VehicleSaleReportTable
                columns={columns}
                items={items}
                totalCount={totalCount}
                loading={isFetching}
                lazyParams={lazyParams}
                onPage={onPage}
                sortField={sortField}
                sortOrder={sortOrder}
                onSortChange={onSort}
                activeFilters={activeFilters}
                onFiltersChange={handleFilterChange}
                filterOptions={filterOptions}
                locale={locale}
            />
        </div>
    );
}
